import React from 'react';
import { isBloggerBusiness } from '../../../utils/accessService';
import { buildImageSource, buildImageSourceSet } from '../../../utils/buildImageSource';
import { formatCurrency } from '../../../utils/format/formatCurrency';
import { getDomainFromUrl } from '../../../utils/getDomainFromUrl/getDomainFromUrl';
import { useTranslation } from '../../../utils/useTranslation';
import { BusinessType } from '../../business/types';
import { IconClose } from '../../icon/IconClose';
import { IconPlus } from '../../icon/IconPlus';
import { IconProduct } from '../../icon/IconProduct';
import { IconButton } from '../../input/IconButton';
import { Flex } from '../../layout/Flex';
import { Stack } from '../../layout/Stack';
import { Image } from '../../media/Image';
import { MediaCover } from '../../media/MediaCover';
import { Retry } from '../../media/Retry';
import { Typography } from '../../typography/Typography';
import { ProductMonetization } from '../ProductMonetization/ProductMonetization';
import { ProductUnavailableLabel } from '../ProductUnavailableLabel/ProductUnavailableLabel';
import { ProductItemUI } from '../types';

interface PostPersistenceProductsProps {
    onRemoveProduct: (productId: string) => void;
    products: ProductItemUI[];
    showAddProductsDrawer: VoidFunction;
    businessType?: BusinessType;
}

export const PostPersistenceProducts = (props: PostPersistenceProductsProps) => {
    const t = useTranslation();
    const { onRemoveProduct, showAddProductsDrawer, products, businessType } = props;
    const isBlogger = isBloggerBusiness(businessType);

    return (
        <Flex width="100%" pb={2}>
            <Stack
                width="100%"
                count={products.length}
                pb={1}
                spacing={1}
                renderItem={(index: number) => {
                    const product = products[index];
                    const media = product.content[0]?.media;
                    const priceInfo = product.priceInfo;
                    const isUnavailable = product.isUnavailable;
                    const hasPriceInfo =
                        !isUnavailable &&
                        priceInfo?.integerPart !== undefined &&
                        priceInfo?.integerPart !== null;
                    const domain = isBlogger ? getDomainFromUrl(product.originalUrl) : undefined;
                    const linkProps = isUnavailable
                        ? {}
                        : { as: 'a', target: '_blank', href: product.url };

                    return (
                        <Flex
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            width="100%"
                            key={product.id}
                            backgroundColor="var(--control-secondary-background)"
                            borderRadius={3}
                            p={0.5}
                        >
                            <Flex direction="row" alignItems="center" spacing={2}>
                                <MediaCover
                                    ratio={1}
                                    height={72}
                                    width={72}
                                    minWidth={72}
                                    borderRadius={2.5}
                                    backgroundColor="var(--background-primary)"
                                    alignSelf="flex-start"
                                    opacity={isUnavailable ? 0.5 : 1}
                                    {...linkProps}
                                >
                                    <Flex
                                        alignItems="center"
                                        justifyContent="center"
                                        backgroundColor="var(--background-primary)"
                                    >
                                        {media ? (
                                            <Retry>
                                                <Image
                                                    objectFit="contain"
                                                    width="100%"
                                                    height="100%"
                                                    src={buildImageSource(media)}
                                                    srcSet={buildImageSourceSet(media)}
                                                />
                                            </Retry>
                                        ) : (
                                            <IconProduct
                                                size={36}
                                                color="var(--control-secondary-background)"
                                            />
                                        )}
                                    </Flex>
                                </MediaCover>
                                <Flex spacing={0.5} py={0.5}>
                                    {product.monetizationEnabled && !isUnavailable ? (
                                        <ProductMonetization color="invert" />
                                    ) : null}
                                    <Typography
                                        variant="body2"
                                        color={isUnavailable ? 'secondary' : 'primary'}
                                        {...linkProps}
                                    >
                                        {t(product.title)}
                                    </Typography>
                                    {isUnavailable ? <ProductUnavailableLabel widthHint /> : null}
                                    {hasPriceInfo ? (
                                        <Typography variant="body" weight="bold" {...linkProps}>
                                            {formatCurrency(
                                                priceInfo.integerPart,
                                                priceInfo.fractionalPart,
                                            )}
                                        </Typography>
                                    ) : null}
                                    {domain ? (
                                        <Typography
                                            variant="caption"
                                            color="var(--color-secondary)"
                                            {...linkProps}
                                        >
                                            {domain}
                                        </Typography>
                                    ) : null}
                                </Flex>
                            </Flex>
                            <IconButton padding="16px" onClick={() => onRemoveProduct(product.id)}>
                                <IconClose />
                            </IconButton>
                        </Flex>
                    );
                }}
            />
            {products.length > 0 && (
                <Flex
                    width="100%"
                    border="2px dashed var(--divider)"
                    direction="row"
                    alignItems="center"
                    justifyContent="center"
                    cursor="pointer"
                    p={1.75}
                    borderRadius={2}
                    spacing={2}
                    onClick={showAddProductsDrawer}
                >
                    <Flex
                        p={1}
                        borderRadius={2.5}
                        backgroundColor="var(--control-secondary-background)"
                    >
                        <IconPlus size={24} />
                    </Flex>
                    <Typography variant="caption" weight="bold">
                        ДОБАВИТЬ ЕЩЕ ТОВАРЫ
                    </Typography>
                </Flex>
            )}
        </Flex>
    );
};
